/**
 * 팀 관련 공통 헬퍼 함수
 * team.service.ts의 중복 코드를 제거하기 위한 유틸리티
 */

import { createBrowserClient } from '@/supabase'
import type { SupabaseClient } from '@supabase/supabase-js'
import { requireActiveProfileId } from '@/utils/profile-helpers'
import { isTeamManager } from '@/utils/team-role'

/**
 * 인증된 사용자와 프로필 정보 조회
 * @returns supabase 클라이언트, 사용자 정보, profile_id
 * @throws Error 로그인되지 않았거나 프로필을 찾을 수 없는 경우
 */
export async function getAuthenticatedUserAndProfile() {
  const supabase = createBrowserClient()
  const { data: { user } } = await supabase.auth.getUser()
  
  if (!user) {
    throw new Error('로그인이 필요합니다.')
  }
  
  const profileId = await requireActiveProfileId(supabase, user.id)
  
  return { supabase, user, profileId }
}

/**
 * 팀 조회 및 매니저 권한 확인
 * @param supabase Supabase 클라이언트
 * @param teamId 팀 ID
 * @param profileId 확인할 profile_id
 * @returns 팀 정보 및 매니저 여부
 * @throws Error 팀을 찾을 수 없는 경우
 */
export async function getTeamWithManagerCheck(
  supabase: SupabaseClient,
  teamId: number,
  profileId: string
) {
  const { data: team, error } = await supabase
    .from('teams')
    .select('id, manager_profile_id')
    .eq('id', teamId)
    .is('deleted_at', null)
    .maybeSingle()
  
  if (error) throw error
  if (!team) throw new Error('팀을 찾을 수 없습니다.')
  
  const isManager = isTeamManager(team, profileId)
  
  return { team, isManager }
}

/**
 * 팀원들의 계정 정보를 배치로 조회
 * @param supabase Supabase 클라이언트
 * @param memberProfileIds 팀원들의 profile_id 배열
 * @returns profile_id를 키로 하는 계정 정보 맵
 */
export async function getTeamMemberAccountsBatch(
  supabase: SupabaseClient,
  memberProfileIds: string[]
): Promise<Map<string, { 
  profile_id: string
  user_id: string
  username: string
  bio?: string | null
  main_job?: string[] | null
  expertise?: string[] | null
  skills?: string[] | null
  badges?: any
}>> {
  if (memberProfileIds.length === 0) {
    return new Map()
  }

  const { data: accounts, error } = await supabase
    .from('accounts')
    .select('profile_id, user_id, username, bio, main_job, expertise, skills, badges')
    .in('profile_id', memberProfileIds)
    .is('deleted_at', null)

  if (error) {
    console.warn('팀원 계정 배치 조회 실패:', error)
    return new Map()
  }

  return new Map(
    (accounts || []).map(acc => [acc.profile_id, acc])
  )
}

/**
 * 팀 상태 상수
 */
export const TEAM_MEMBER_STATUS = {
  ACTIVE: 'active',
  PENDING: 'pending',
  PENDING_LEAVE: 'pending_leave',
  INACTIVE: 'inactive',
  DECLINED: 'declined'
} as const

export type TeamMemberStatus = typeof TEAM_MEMBER_STATUS[keyof typeof TEAM_MEMBER_STATUS]

/**
 * 로깅 유틸리티 (개발 환경에서만)
 */
const isDev = process.env.NODE_ENV === 'development'

export function logTeamService(
  level: 'info' | 'warn' | 'error',
  context: string,
  data?: any
) {
  if (!isDev) return
  
  const prefix = `[team.service:${context}]`
  switch (level) {
    case 'info':
      console.log(prefix, data)
      break
    case 'warn':
      console.warn(prefix, data)
      break
    case 'error':
      console.error(prefix, data)
      break
  }
}

